import { useEffect, useRef, useState } from 'react'
import { flexRender, useReactTable, type Column, type Row, type Table } from '@tanstack/react-table'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { Download, FileSpreadsheet, FileText, Loader2 } from 'lucide-react'

import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { columnLabel } from '@/components/ColumnPicker'
import { apiErrorDescription } from '@/services/core/apiError'
import type { AllPages } from '@/services/core/paging'
import { formatCount } from '@/utils/format'
import { exportToCSV, exportToExcel } from '@/utils/importExport'

type Format = 'csv' | 'excel' | 'excelTotals'

interface TableDownloadMenuProps<TData> {
  table: Table<TData>
  /** Base filename, without extension or date. */
  filename: string
  /** The rendered table, to read display-only columns off their cells. */
  getTableElement?: () => HTMLTableElement | null
  /** Every row of a paged list, to download all its pages rather than the one shown. */
  fetchAll?: () => Promise<AllPages<TData>>
}

/** A cell's text as it reads on screen, its lines joined on one line. */
function cellText(cell: Element | null | undefined): string {
  const text = (cell as HTMLElement | null)?.innerText ?? ''
  return text
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean)
    .join(' · ')
}

/** Whether a column goes in a download at all. */
function isExported<TData>(column: Column<TData, unknown>) {
  return column.id !== 'select' && column.columnDef.meta?.export !== false
}

/** A column with no field behind it, whose value is the text its cell shows. */
function isDisplayOnly<TData>(column: Column<TData, unknown>) {
  return !column.accessorFn && !column.columnDef.meta?.exportValue
}

/**
 * Fetched rows laid out off screen, under the list's own columns and state,
 * so their display-only cells can be read just like those of the page shown.
 * Only those cells are rendered; the rest export straight from their fields.
 */
function OffscreenRows<TData>({
  table,
  data,
  onRendered,
}: {
  table: Table<TData>
  data: TData[]
  onRendered: (rows: Row<TData>[], tableElement: HTMLTableElement | null) => void
}) {
  const tableRef = useRef<HTMLTableElement>(null)
  const rendered = useRef(false)
  const offscreen = useReactTable({
    ...table.options,
    data,
    state: { ...table.getState(), rowSelection: {} },
    onStateChange: () => {},
    // The handlers are the list's own, so nothing here may reset its state.
    autoResetAll: false,
  })
  const rows = offscreen.getRowModel().flatRows

  useEffect(() => {
    // Once only, or strict mode's second run of the effect downloads twice.
    if (rendered.current) return
    rendered.current = true
    onRendered(rows, tableRef.current)
  }, [rows, onRendered])

  return (
    <table ref={tableRef} aria-hidden className="sr-only">
      <tbody>
        {rows.map((row) => (
          <tr key={row.id} data-row-id={row.id}>
            {row
              .getVisibleCells()
              .filter((cell) => isExported(cell.column) && isDisplayOnly(cell.column))
              .map((cell) => (
                <td key={cell.id} data-column-id={cell.column.id}>
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </td>
              ))}
          </tr>
        ))}
      </tbody>
    </table>
  )
}

/**
 * Downloads a list as it stands: the rows it shows, in their order, under
 * every column that is switched on — or, for a paged list that can fetch them,
 * the rows of every page. A column gives its `exportValue`, else its
 * field, else — for a display-only column such as a status or a timeline —
 * the text its cell shows. The selection checkbox, columns that opt out, and
 * display-only columns with nothing to read, such as action buttons, are left out.
 */
export function TableDownloadMenu<TData>({
  table,
  filename,
  getTableElement,
  fetchAll,
}: TableDownloadMenuProps<TData>) {
  const { t } = useTranslation()
  const [fetching, setFetching] = useState(false)
  const [fetched, setFetched] = useState<{ format: Format; data: TData[] } | null>(null)

  const exportColumns = () => table.getVisibleLeafColumns().filter(isExported)
  const hasTotals = exportColumns().some((c) => c.columnDef.meta?.exportTotal !== undefined)

  const rowsToExport = (rows: Row<TData>[], tableElement: HTMLTableElement | null | undefined) => {
    const valueOf = (row: Row<TData>, column: Column<TData, unknown>): unknown => {
      const exportValue = column.columnDef.meta?.exportValue
      if (exportValue) return exportValue(row.original)
      if (column.accessorFn) return row.getValue(column.id)
      const rowElement = tableElement?.querySelector(`tr[data-row-id="${CSS.escape(row.id)}"]`)
      return cellText(rowElement?.querySelector(`td[data-column-id="${CSS.escape(column.id)}"]`))
    }

    const values = exportColumns().map((column) => ({
      column,
      values: rows.map((row) => valueOf(row, column)),
    }))
    // A display-only column that reads empty throughout holds only icons or buttons.
    const columns = values.filter(
      ({ column, values }) => !isDisplayOnly(column) || values.some((v) => v !== '' && v != null)
    )
    const hasIdColumn = columns.some(({ column }) => column.id === 'id')
    return rows.map((row, i) => {
      const out: Record<string, unknown> = {}
      const id = (row.original as { id?: unknown }).id
      if (!hasIdColumn && typeof id === 'string') out[t('common.id')] = id
      for (const { column, values } of columns) out[columnLabel(column)] = values[i]
      return out
    })
  }

  /** The footer totals, labelled in the first column that has none of its own. */
  const totalsToExport = (firstKey: string | undefined) => {
    const out: Record<string, unknown> = {}
    for (const column of exportColumns()) {
      const total = column.columnDef.meta?.exportTotal
      if (total !== undefined) out[columnLabel(column)] = total
    }
    if (firstKey && !(firstKey in out)) out[firstKey] = t('importExport.total')
    return out
  }

  const save = (
    format: Format,
    tableRows: Row<TData>[],
    tableElement: HTMLTableElement | null | undefined
  ) => {
    const name = `${filename}-${new Date().toISOString().slice(0, 10)}`
    const rows = rowsToExport(tableRows, tableElement)
    if (format === 'csv') exportToCSV(rows, name)
    else if (format === 'excel') void exportToExcel(rows, name)
    else {
      const totals = totalsToExport(Object.keys(rows[0] ?? {})[0])
      void exportToExcel(rows, name, {}, undefined, undefined, totals)
    }
  }

  const download = async (format: Format) => {
    if (!fetchAll) return save(format, table.getRowModel().flatRows, getTableElement?.())
    setFetching(true)
    try {
      const { results, truncated, totalCount } = await fetchAll()
      if (truncated) {
        toast.warning(t('importExport.downloadTruncated'), {
          description:
            totalCount !== undefined
              ? t('importExport.downloadTruncatedDetail', {
                  shown: formatCount(results.length),
                  total: formatCount(totalCount),
                })
              : t('importExport.downloadTruncatedUnknown', { shown: formatCount(results.length) }),
          duration: 10000,
        })
      }
      setFetched({ format, data: results })
    } catch (e) {
      toast.error(t('common.error'), { description: apiErrorDescription(e), duration: 5000 })
      setFetching(false)
    }
  }

  const saveFetched = (rows: Row<TData>[], tableElement: HTMLTableElement | null) => {
    if (fetched) save(fetched.format, rows, tableElement)
    setFetched(null)
    setFetching(false)
  }

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="outline"
            size="icon-sm"
            aria-label={t('importExport.download')}
            title={t('importExport.download')}
            disabled={fetching || !table.getRowModel().rows.length}
          >
            {fetching ? (
              <Loader2 className="size-4 animate-spin" />
            ) : (
              <Download className="size-4" />
            )}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem onClick={() => void download('excel')}>
            <FileSpreadsheet className="size-4" />
            {t('importExport.downloadExcel')}
          </DropdownMenuItem>
          {hasTotals && (
            <DropdownMenuItem onClick={() => void download('excelTotals')}>
              <FileSpreadsheet className="size-4" />
              {t('importExport.downloadExcelTotals')}
            </DropdownMenuItem>
          )}
          <DropdownMenuItem onClick={() => void download('csv')}>
            <FileText className="size-4" />
            {t('importExport.downloadCSV')}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      {fetched && <OffscreenRows table={table} data={fetched.data} onRendered={saveFetched} />}
    </>
  )
}
